<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Edit'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-ai-invoice-accounting-value-types' }">
            {{ $t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES") }}
          </router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:body>
        <b-form>
          <FormBuilder
            v-if="!firstLoader"
            :form="form"
            :schemaJson="schemaJson"
            :v="$v"
          ></FormBuilder>
        </b-form>

        <div
          v-if="
            checkIfUserHasPermission(['ai-invoice-accounting-values-show']) &&
              !firstLoader
          "
          class="mt-5"
        >
          <div class="card card-custom">
            <div class="card-header">
              <div class="card-title">
                <h3 class="card-label">
                  {{ $t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUES") }}
                </h3>
              </div>
              <div class="card-toolbar">
                <button
                  v-if="
                    checkIfUserHasPermission([
                      'ai-invoice-accounting-values-create'
                    ])
                  "
                  type="button"
                  @click="openAddValueModal"
                  class="btn btn-light-success font-weight-bolder"
                >
                  <i class="flaticon2-plus"></i> {{ $t("LABELS.ADD_NEW") }}
                </button>
              </div>
            </div>
            <div class="card-body">
              <v-data-table
                :headers="valuesHeaders"
                :items="accountingValues"
                class="elevation-1"
              >
                <template v-slot:[`item.actions`]="{ item }">
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="
                          checkIfUserHasPermission([
                            'ai-invoice-accounting-values-update'
                          ])
                        "
                        class="mr-2"
                        fab
                        dark
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        @click="editValue(item)"
                      >
                        <v-icon dark>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("LABELS.EDIT") }}</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:foot>
        <div
          v-show="_.size(errors) > 0"
          role="alert"
          class="alert alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingAiInvoiceAccountingValueType"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>

    <v-dialog v-model="showAddValueModal" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{
            editingValueId ? $t("LABELS.EDIT") : $t("LABELS.ADD_NEW")
          }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="valueForm" v-model="valueFormValid" lazy-validation>
            <v-text-field
              v-model="newValue.name"
              :label="$t('FORM_LABELS.NAME')"
              required
              :rules="[
                v => !!v || $t('FORM.VALID_NAME'),
                v =>
                  !v || v.length <= 255 || 'Name must not exceed 255 characters'
              ]"
            ></v-text-field>
            <v-text-field
              v-model="newValue.code"
              :label="$t('FORM_LABELS.CODE')"
              required
              :rules="[
                v => !!v || $t('FORM.VALID_CODE'),
                v =>
                  !v || v.length <= 255 || 'Code must not exceed 255 characters'
              ]"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeValueModal">
            {{ $t("LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            @click="saveValue"
            :loading="isLoadingAiInvoiceAccountingValue"
          >
            {{ $t("LABELS.SAVE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import FormBuilder from "@/view/content/forms/FormBuilder.vue";
import FormInput from "@/view/content/forms/components/FormInput.vue";
import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import { validationMixin } from "vuelidate";
import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";
import KTCard from "@/view/content/Card.vue";
import { mapGetters, mapState } from "vuex";
import {
  FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPE,
  UPDATE_AI_INVOICE_ACCOUNTING_VALUE_TYPE
} from "../store/ai-invoice-accounting-value-types.module";
import {
  SAVE_AI_INVOICE_ACCOUNTING_VALUE,
  UPDATE_AI_INVOICE_ACCOUNTING_VALUE
} from "@/modules/ai-Invoice-accounting-values/store/ai-invoice-accounting-values.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import { FETCH_AI_INVOICE_TYPES } from "@/modules/ai-invoices/store/ai-invoices.module";

export default {
  mixins: [validationMixin, formBuilderMixin, permissionMixin],
  data() {
    return {
      errors: {},
      id: null,
      accountingValues: [],
      showAddValueModal: false,
      valueFormValid: false,
      newValue: {
        name: "",
        code: ""
      },
      editingValueId: null,
      invoiceTypes: [],
      valuesHeaders: [
        { text: this.$i18n.t("FORM_LABELS.NAME"), value: "name" },
        { text: this.$i18n.t("FORM_LABELS.CODE"), value: "code" },
        {
          text: this.$i18n.t("FORM_LABELS.ACTIONS"),
          value: "actions",
          sortable: false,
          align: "right"
        }
      ],
      schemaJson: {
        fields: [
          {
            type: "input",
            inputType: "text",
            id: "name-input",
            groupId: "name-group",
            required: "required",
            feedback_id: "name-live-feedback",
            divider: false,
            i18n: {
              label: "FORM_LABELS.NAME",
              placeholder: "FORM_PLACEHOLDERS.NAME",
              validation: "FORM.VALID_NAME"
            },
            validations: {
              required: true,
              minLength: 2
            },
            translatable: false,
            model: "name"
          },
          {
            type: "input",
            inputType: "text",
            id: "code-input",
            groupId: "code-group",
            required: "required",
            feedback_id: "code-live-feedback",
            divider: false,
            i18n: {
              label: "FORM_LABELS.CODE",
              placeholder: "FORM_PLACEHOLDERS.CODE",
              validation: "FORM.VALID_CODE"
            },
            validations: {
              required: true,
              minLength: 1
            },
            translatable: false,
            model: "code"
          },
          {
            type: "select",
            id: "ai-invoice-type-select",
            groupId: "ai-invoice-type-group",
            required: "required",
            feedback_id: "ai-invoice-type-live-feedback",
            divider: false,
            i18n: {
              label: "MENU.ITEM.EXP.AI_INVOICE_TYPE",
              placeholder: "FORM_PLACEHOLDERS.SELECT_INVOICE_TYPE",
              validation: "FORM.VALID_INVOICE_TYPE"
            },
            validations: {
              required: true
            },
            translatable: false,
            model: "aiInvoiceTypeIds",
            options: [],
            multiple: true
          }
        ]
      }
    };
  },
  components: {
    KTCard,
    FormBuilder,
    SaveButtonDropdown,
    FormInput
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES"),
        route: { name: "list-ai-invoice-accounting-value-types" }
      },
      { title: this.$i18n.t("MENU.ITEM.EDIT") }
    ]);

    this.$store
      .dispatch(FETCH_AI_INVOICE_TYPES)
      .then(data => {
        this.invoiceTypes = Array.isArray(data) ? data : data.data || [];
        const typeField = this.schemaJson.fields.find(
          f => f.model === "aiInvoiceTypeIds"
        );
        if (typeField && this.invoiceTypes.length > 0) {
          this.$set(
            typeField,
            "options",
            this.invoiceTypes.map(t => ({
              id: t.id,
              name: t.name
            }))
          );
        }
      })
      .catch(error => {
        console.error("Failed to fetch invoice types:", error);
      });

    let id = this.$route.params.id;
    this.$store
      .dispatch(FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPE, id)
      .then(data => {
        this.id = data.data.id;
        const transformedData = {
          ...data.data,
          aiInvoiceTypeIds: data.data.invoiceTypes
            ? data.data.invoiceTypes.map(type => type.id)
            : []
        };
        this.generateFormOutOfSchemaJson(this.schemaJson, transformedData);
        this.accountingValues = data.data.values || [];
      });
  },
  computed: {
    ...mapGetters([
      "isLoadingAiInvoiceAccountingValueType",
      "isLoadingAiInvoiceAccountingValue"
    ]),
    ...mapState({
      errors: state => state.ai_invoice_accounting_value_types_errors
    })
  },
  validations() {
    let tmpValidationObject = this.generateFormValidationsOutOfSchemaJson(
      this.schemaJson
    );
    return tmpValidationObject;
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit(evt) {
      evt.preventDefault();
    },
    onSave(next) {
      let vm = this;
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> Warning",
          text: "Please fill all required fields correctly"
        });
        return;
      }

      const payload = {
        name: this.form.name,
        code: this.form.code,
        aiInvoiceTypeIds: Array.isArray(this.form.aiInvoiceTypeIds)
          ? this.form.aiInvoiceTypeIds
          : this.form.aiInvoiceTypeIds
          ? [this.form.aiInvoiceTypeIds]
          : []
      };

      this.$store
        .dispatch(UPDATE_AI_INVOICE_ACCOUNTING_VALUE_TYPE, {
          id: this.id,
          payload: payload
        })
        .then(data => {
          vm.errors = data.data.errors;
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          if (next == "continue") {
            //Continue editing
          }
          if (next == "new") {
            this.$router.push({
              name: "add-ai-invoice-accounting-value-types"
            });
          }
          if (next == "exit") {
            this.$router.push({
              name: "list-ai-invoice-accounting-value-types"
            });
          }
        })
        .catch(response => {
          vm.errors = response.data.errors;
          vm.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: response.data.message
          });
        });
    },
    openAddValueModal() {
      this.editingValueId = null;
      this.newValue = {
        name: "",
        code: ""
      };
      if (this.$refs.valueForm) {
        this.$refs.valueForm.reset();
      }
      this.showAddValueModal = true;
    },
    closeValueModal() {
      this.showAddValueModal = false;
      this.editingValueId = null;
      this.newValue = {
        name: "",
        code: ""
      };
      if (this.$refs.valueForm) {
        this.$refs.valueForm.reset();
      }
    },
    saveValue() {
      if (!this.$refs.valueForm || !this.$refs.valueForm.validate()) {
        return;
      }

      const payload = {
        name: this.newValue.name,
        code: this.newValue.code,
        ai_invoice_accounting_value_type_id: this.id
      };

      if (this.editingValueId) {
        this.$store
          .dispatch(UPDATE_AI_INVOICE_ACCOUNTING_VALUE, {
            id: this.editingValueId,
            payload: payload
          })
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text: data.message || "Value updated successfully"
            });
            const index = this.accountingValues.findIndex(
              v => v.id === this.editingValueId
            );
            if (index !== -1) {
              this.$set(this.accountingValues, index, data.data);
            }
            this.closeValueModal();
          })
          .catch(response => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data?.message || "Failed to update value"
            });
          });
      } else {
        this.$store
          .dispatch(SAVE_AI_INVOICE_ACCOUNTING_VALUE, payload)
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text: data.message || "Value created successfully"
            });
            this.accountingValues.push(data.data);
            this.closeValueModal();
          })
          .catch(response => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data?.message || "Failed to create value"
            });
          });
      }
    },
    editValue(item) {
      this.editingValueId = item.id;
      this.newValue = {
        name: item.name,
        code: item.code
      };
      this.showAddValueModal = true;
    },
    resetVForm() {
      this.$v.$reset();
    }
  }
};
</script>
